export type ToolActivityStatus = "running" | "complete" | "error";

export interface ToolActivityContent {
  kind: "knowledge_lookup" | "web_search";
  callId?: string;
  generationId?: string;
  concept: string;
  source: string;
  toolType?: string;
  status: ToolActivityStatus;
  resultsCount?: number;
  errorMessage?: string;
}

export interface ToolActivityUpdate {
  messageId: string;
  content: ToolActivityContent;
  processingStatus: "processing" | "complete" | "error";
}

const cleanText = (value: unknown, fallback: string): string => {
  if (typeof value !== "string") return fallback;
  const cleaned = value.replace(/\s+/g, " ").trim();
  return cleaned || fallback;
};

const eventIdentity = (event: Record<string, unknown>): string | null => {
  const value = event.call_id || event.tool_id;
  return typeof value === "string" && value.trim() ? value.trim() : null;
};

/**
 * Convert Agent6's correlated tool lifecycle events into one stable UI row.
 * Start and completion events intentionally resolve to the same message ID so
 * the completed state replaces the spinner instead of adding another row.
 */
export const toolActivityUpdateFromEvent = (
  event: Record<string, unknown>,
  generationId: string
): ToolActivityUpdate | null => {
  const type = event.type;
  if (
    type !== "knowledge_search_started" &&
    type !== "knowledge_search_completed"
  ) {
    return null;
  }

  const identity = eventIdentity(event);
  if (!identity) return null;

  const eventStatus =
    type === "knowledge_search_started"
      ? "running"
      : event.status === "failed"
        ? "error"
        : "complete";
  const resultsCount =
    typeof event.results_count === "number"
      ? Math.max(0, event.results_count)
      : undefined;

  return {
    messageId: `tool-activity-${generationId}-${identity}`,
    content: {
      kind: "knowledge_lookup",
      callId: identity,
      generationId,
      concept: cleanText(event.concept, "supporting evidence"),
      source: cleanText(
        event.collection_name || event.source_type,
        "Textbooks"
      ),
      toolType: cleanText(event.source_type, "textbooks"),
      status: eventStatus,
      resultsCount,
      errorMessage:
        eventStatus === "error"
          ? cleanText(event.error_message, "Search could not be completed")
          : undefined,
    },
    processingStatus:
      eventStatus === "running"
        ? "processing"
        : eventStatus === "error"
          ? "error"
          : "complete",
  };
};

export const getToolActivityCallId = (content: unknown): string | null => {
  if (!content || typeof content !== "object") return null;
  const value = (content as Record<string, unknown>).callId;
  return typeof value === "string" && value.trim() ? value.trim() : null;
};

export interface ToolActivityDisplay {
  concept: string;
  source: string;
  status: ToolActivityStatus;
  label: string;
}

export const getToolActivityDisplay = (
  content: unknown
): ToolActivityDisplay => {
  const value =
    content && typeof content === "object"
      ? (content as Record<string, unknown>)
      : {};
  const isWebSearch = value.kind === "web_search";
  const concept = cleanText(
    isWebSearch ? value.query : value.concept,
    isWebSearch ? "current evidence" : "supporting evidence"
  );
  const source = cleanText(
    isWebSearch
      ? "Web"
      : value.source || value.collection || value.toolType,
    "Sources"
  );
  const status: ToolActivityStatus =
    value.status === "running" || value.status === "error"
      ? value.status
      : "complete";
  const resultsCount =
    typeof value.resultsCount === "number"
      ? Math.max(0, value.resultsCount)
      : undefined;

  let label: string;
  if (status === "running") {
    label = isWebSearch
      ? `Searching the web for ${concept}`
      : `Searching for ${concept}`;
  } else if (status === "error") {
    label = `Couldn’t complete ${concept}`;
  } else if (resultsCount === 0) {
    label = `No direct matches for ${concept}`;
  } else if (resultsCount !== undefined) {
    label = `Found ${resultsCount} ${resultsCount === 1 ? "source" : "sources"} for ${concept}`;
  } else if (isWebSearch) {
    label = `Searched the web for ${concept}`;
  } else {
    label = `Reviewed ${concept}`;
  }

  return { concept, source, status, label };
};
